import colors from '@/theme/colors';
import { TContent, TContentPublishState, TContentStatus } from '@/types/Content.types';
import { Text } from '@chakra-ui/react';

export type TContentStatusBadgeValue = TContentStatus | TContentPublishState;

// Label and fill per state. Solid pill, white label, no icon — kept identical to the mobile
// badge, which is the whole point of this component existing twice.
//
// Fills are the brand tokens, specified by design. Note white on `orange` is 2.4:1 and on
// `teal` is 1.9:1 against white, both below the 4.5:1 WCAG AA needs, and these sit over
// arbitrary thumbnails. Recorded rather than adjusted: the values are the design's.
const STATUS_DISPLAY: Record<TContentStatusBadgeValue, { label: string; fill: string }> = {
  draft: { label: 'Draft', fill: colors.extra.orange },
  // `publishing` is the backend's name for draft + publishOnReady — the video is queued and
  // goes live on its own once Mux finishes. `idle` cannot reach the badge in practice, but
  // the union requires it, and it describes the same situation.
  idle: { label: 'Publishing', fill: colors.primary[500] },
  publishing: { label: 'Publishing', fill: colors.primary[500] },
  failed: { label: 'Failed', fill: colors.extra.red },
  removed: { label: 'Removed', fill: colors.extra.red },
  withdrawn: { label: 'Withdrawn', fill: colors.extra.red },
  published: { label: 'Published', fill: colors.extra.teal },
};

// What the badge should read for one item on a creator's own shelf, or `null` for no badge.
//
// Published content carries no badge: it is the expected state for most of a shelf, so
// labelling it turns the badge into decoration and buries the items that actually want
// attention. The `published` style above exists for surfaces that do want to state it.
//
// A video still processing is technically a draft, but "Draft" is misleading while it is
// queued to go live on its own — it shows its publish state instead.
export const getContentDisplayStatus = (content: TContent): TContentStatusBadgeValue | null => {
  if (content.status === 'published') return null;

  const isAwaitingPublish =
    content.type === 'video' &&
    content.status === 'draft' &&
    (content.publishOnReady || content.publishState !== 'idle');

  return isAwaitingPublish ? content.publishState : content.status;
};

const ContentStatusBadge = ({ status }: { status?: TContentStatusBadgeValue | null }) => {
  if (!status) return null;
  const { label, fill } = STATUS_DISPLAY[status];

  return (
    <Text
      as="span"
      display="inline-block"
      backgroundColor={fill}
      color={colors.extra.white}
      borderRadius="999px"
      paddingX="14px"
      paddingY="6px"
      fontSize="13px"
      fontWeight={700}
      lineHeight="1.15"
      whiteSpace="nowrap"
    >
      {label}
    </Text>
  );
};

export default ContentStatusBadge;
